// POSIX TZ string evaluation (the TZif footer), e.g. "EST5EDT,M3.2.0,M11.1.0".
// Handles the tzdata extensions: <...> quoted names, hours outside 0..24 in
// rule times (e.g. "/-1" or "/25"), negative DST (Europe/Dublin).

export interface PosixType {
  utoff: number; // seconds east of UTC
  isdst: 0 | 1;
  abbr: string;
}

interface Rule {
  kind: "M" | "J" | "n";
  m?: number;
  w?: number;
  d?: number;
  n?: number;
  time: number; // seconds after local midnight (may be negative or > 86400)
}

export interface PosixTz {
  std: PosixType;
  dst?: PosixType;
  start?: Rule;
  end?: Rule;
}

const DAY = 86400;

function parseName(s: string, p: { i: number }): string {
  if (s[p.i] === "<") {
    const j = s.indexOf(">", p.i);
    if (j < 0) throw new Error(`bad TZ name in ${s}`);
    const name = s.slice(p.i + 1, j);
    p.i = j + 1;
    return name;
  }
  const m = /^[A-Za-z]+/.exec(s.slice(p.i));
  if (!m) throw new Error(`bad TZ name in ${s}`);
  p.i += m[0].length;
  return m[0];
}

// [+-]hh[:mm[:ss]] -> seconds
function parseHms(s: string, p: { i: number }): number {
  const m = /^([+-]?)(\d{1,3})(?::(\d{1,2})(?::(\d{1,2}))?)?/.exec(s.slice(p.i));
  if (!m) throw new Error(`bad TZ time in ${s}`);
  p.i += m[0].length;
  const v = +m[2] * 3600 + +(m[3] ?? 0) * 60 + +(m[4] ?? 0);
  return m[1] === "-" ? -v : v;
}

function parseRule(s: string, p: { i: number }): Rule {
  let rule: Rule;
  if (s[p.i] === "M") {
    const m = /^M(\d{1,2})\.(\d)\.(\d)/.exec(s.slice(p.i));
    if (!m) throw new Error(`bad TZ rule in ${s}`);
    p.i += m[0].length;
    rule = { kind: "M", m: +m[1], w: +m[2], d: +m[3], time: 7200 };
  } else if (s[p.i] === "J") {
    const m = /^J(\d{1,3})/.exec(s.slice(p.i));
    if (!m) throw new Error(`bad TZ rule in ${s}`);
    p.i += m[0].length;
    rule = { kind: "J", n: +m[1], time: 7200 };
  } else {
    const m = /^(\d{1,3})/.exec(s.slice(p.i));
    if (!m) throw new Error(`bad TZ rule in ${s}`);
    p.i += m[0].length;
    rule = { kind: "n", n: +m[1], time: 7200 };
  }
  if (s[p.i] === "/") {
    p.i++;
    rule.time = parseHms(s, p);
  }
  return rule;
}

const cache = new Map<string, PosixTz | null>();

export function parsePosix(s: string): PosixTz | null {
  if (cache.has(s)) return cache.get(s)!;
  let out: PosixTz | null = null;
  try {
    const p = { i: 0 };
    const stdName = parseName(s, p);
    const stdOff = 0 - parseHms(s, p); // POSIX offsets are west-positive
    const tz: PosixTz = { std: { utoff: stdOff, isdst: 0, abbr: stdName } };
    if (p.i < s.length && s[p.i] !== ",") {
      const dstName = parseName(s, p);
      let dstOff = stdOff + 3600;
      if (p.i < s.length && s[p.i] !== ",") dstOff = 0 - parseHms(s, p);
      tz.dst = { utoff: dstOff, isdst: 1, abbr: dstName };
      if (s[p.i] === ",") {
        p.i++;
        tz.start = parseRule(s, p);
        if (s[p.i] !== ",") throw new Error(`bad TZ rule in ${s}`);
        p.i++;
        tz.end = parseRule(s, p);
      } else {
        // DST with no rules: POSIX default is US rules; tzdata never emits this.
        tz.start = { kind: "M", m: 3, w: 2, d: 0, time: 7200 };
        tz.end = { kind: "M", m: 11, w: 1, d: 0, time: 7200 };
      }
    }
    out = tz;
  } catch {
    out = null;
  }
  cache.set(s, out);
  return out;
}

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m is 1-based

// Seconds since epoch of local midnight at the start of the rule's day in `year`
// (treating local time as if it were UTC).
function ruleDayStart(rule: Rule, year: number): number {
  if (rule.kind === "M") {
    const first = Date.UTC(year, rule.m! - 1, 1);
    const firstDow = new Date(first).getUTCDay();
    let day = 1 + ((rule.d! - firstDow + 7) % 7) + (rule.w! - 1) * 7;
    const dim = daysInMonth(year, rule.m!);
    while (day > dim) day -= 7;
    return Date.UTC(year, rule.m! - 1, day) / 1000;
  }
  if (rule.kind === "J") {
    // Julian day 1..365, Feb 29 never counted
    let n = rule.n!;
    if (isLeap(year) && n >= 60) n += 1;
    return Date.UTC(year, 0, n) / 1000;
  }
  return Date.UTC(year, 0, rule.n! + 1) / 1000; // zero-based day of year
}

export interface PosixTransition {
  at: number; // UTC seconds
  type: PosixType;
}

/** The two transitions of `year` (in UTC), sorted. Empty if the TZ has no DST. */
export function posixTransitionsForYear(tz: PosixTz, year: number): PosixTransition[] {
  if (!tz.dst || !tz.start || !tz.end) return [];
  // Start of DST: local time is in standard time just before the transition.
  const startUtc = ruleDayStart(tz.start, year) + tz.start.time - tz.std.utoff;
  const endUtc = ruleDayStart(tz.end, year) + tz.end.time - tz.dst.utoff;
  const list = [
    { at: startUtc, type: tz.dst },
    { at: endUtc, type: tz.std },
  ];
  list.sort((a, b) => a.at - b.at);
  return list;
}

export function posixTypeAt(tz: PosixTz, utc: number): PosixType {
  if (!tz.dst) return tz.std;
  const year = new Date(utc * 1000).getUTCFullYear();
  // Consider the neighbouring years too: a transition near New Year may
  // belong to the previous year's rule set.
  let current: PosixType | null = null;
  let latest = -Infinity;
  for (const y of [year - 1, year, year + 1]) {
    for (const t of posixTransitionsForYear(tz, y)) {
      if (t.at <= utc && t.at > latest) {
        latest = t.at;
        current = t.type;
      }
    }
  }
  if (current) return current;
  // Before any transition we can see: whichever type is in effect at the end
  // of the previous year's cycle.
  const prev = posixTransitionsForYear(tz, year - 2);
  return prev.length ? prev[prev.length - 1].type : tz.std;
}

export { DAY };
